import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class AlAnfarCompleteSet extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-complete-set-button]');
    this.label = this.querySelector('[data-complete-set-label]');
    this.status = this.querySelector('[data-complete-set-status]');
    this.items = this.#readItems();
    this.defaultLabel = this.label?.textContent || 'Add complete collection';

    this.button?.addEventListener('click', this.#addSet);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#addSet);
  }

  #readItems() {
    const source = this.querySelector('[data-complete-set-items]');
    if (!source) return [];

    try {
      return JSON.parse(source.textContent || '[]').filter((item) => item.available && item.id);
    } catch {
      return [];
    }
  }

  #setState(label, disabled) {
    if (this.label) this.label.textContent = label;
    if (this.button) this.button.disabled = disabled;
  }

  #addSet = async () => {
    if (!this.items.length || !this.button) return;

    this.#setState('Adding the collection…', true);
    if (this.status) this.status.textContent = 'Adding the complete collection to your bag.';

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    const lines = this.items.map((item) => ({
      merchandiseId: String(item.id),
      quantity: item.quantity,
    }));

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines,
      promise: deferred.promise,
    }));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: this.items.map((item) => ({ id: item.id, quantity: item.quantity })),
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'The collection could not be added.');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          itemCount: this.items.length,
          items: cart.items,
          sections: result.sections,
          source: 'al-anfar-complete-set',
        },
      });

      this.#setState('Collection added', true);
      if (this.status) this.status.textContent = 'The complete collection is in your bag.';

      window.setTimeout(() => this.#setState(this.defaultLabel, false), 1600);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({
        error: error instanceof Error ? error.message : 'The collection could not be added.',
        code: 'INVALID',
      }));
      this.#setState('Try again', false);
      if (this.status) {
        this.status.textContent = error instanceof Error
          ? error.message
          : 'The collection could not be added.';
      }
    }
  };
}

if (!customElements.get('al-anfar-complete-set')) {
  customElements.define('al-anfar-complete-set', AlAnfarCompleteSet);
}
